import { Variants } from "framer-motion";

export const ANIMATION_DURATIONS = {
  instant: 0.15,
  fast: 0.3,
  medium: 0.6,
  slow: 0.8,
  extra_slow: 1.2,
  menu_open: 1.4,
  menu_close: 0.7,
} as const;

export const ANIMATION_DELAYS = {
  none: 0,
  micro: 0.05,
  short: 0.2,
  medium: 0.4,
  long: 1.2,
  extra_long: 1.4,
  fade_in: 1.6,
} as const;

export const EASING = {
  ease_in_out: [0.25, 0.46, 0.45, 0.94] as const,
  ease_out: [0.0, 0.0, 0.2, 1.0] as const,
  ease_in: [0.4, 0.0, 1.0, 1.0] as const,
  custom_bounce: [0.68, -0.55, 0.265, 1.55] as const,
  smooth: [0.25, 0.46, 0.45, 0.94] as const,
  elastic: [0.175, 0.885, 0.32, 1.275] as const,
  back: [0.68, -0.55, 0.265, 1.55] as const,
  anticipate: [0.175, 0.885, 0.32, 1.275] as const,
} as const;

export const SPRING_CONFIG = {
  gentle: {
    type: "spring" as const,
    stiffness: 200,
    damping: 20,
    mass: 0.8,
  },
  bouncy: {
    type: "spring" as const,
    stiffness: 300,
    damping: 15,
    mass: 0.6,
  },
  snappy: {
    type: "spring" as const,
    stiffness: 400,
    damping: 25,
    mass: 0.5,
  },
  wobbly: {
    type: "spring" as const,
    stiffness: 180,
    damping: 12,
    mass: 1,
  },
} as const;

export const STAGGER = {
  children_delay: 1.2,
  children_stagger: 0.12,
  children_reverse_stagger: 0.08,
  micro_stagger: 0.04,
} as const;

export const hideNavItemsVariant: Variants = {
  opened: {
    opacity: 0,
    y: "-120%",
    scale: 0.8,
    transition: {
      duration: ANIMATION_DURATIONS.medium,
      ease: EASING.ease_in_out,
    },
  },
  closed: {
    opacity: 1,
    y: "0%",
    scale: 1,
    transition: {
      delay: ANIMATION_DELAYS.extra_long,
      duration: ANIMATION_DURATIONS.medium,
      ease: EASING.elastic,
    },
  },
};

export const mobileMenuVariant: Variants = {
  opened: {
    y: "0%",
    transition: {
      delay: ANIMATION_DELAYS.short,
      duration: ANIMATION_DURATIONS.menu_open,
      ease: EASING.back,
    },
  },
  closed: {
    y: "-100%",
    transition: {
      delay: ANIMATION_DELAYS.medium,
      duration: ANIMATION_DURATIONS.menu_close,
      ease: EASING.anticipate,
    },
  },
};

export const fadeInVariant: Variants = {
  opened: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      delay: ANIMATION_DELAYS.fade_in,
      ...SPRING_CONFIG.gentle,
    },
  },
  closed: {
    opacity: 0,
    scale: 0.9,
    y: 20,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.ease_in,
    },
  },
};

export const ulVariant: Variants = {
  opened: {
    transition: {
      delayChildren: STAGGER.children_delay,
      staggerChildren: STAGGER.children_stagger,
    },
  },
  closed: {
    transition: {
      staggerChildren: STAGGER.children_reverse_stagger,
      staggerDirection: -1,
    },
  },
};

export const liVariant: Variants = {
  opened: {
    opacity: 1,
    y: "0%",
    x: 0,
    scale: 1,
    rotateX: 0,
    transition: {
      ...SPRING_CONFIG.bouncy,
    },
  },
  closed: {
    opacity: 0,
    y: "150%",
    x: -30,
    scale: 0.8,
    rotateX: -45,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.ease_in_out,
    },
  },
};

export const logoVariant: Variants = {
  opened: {
    scale: 0.95,
    rotate: 3,
    y: 2,
    transition: {
      ...SPRING_CONFIG.gentle,
    },
  },
  closed: {
    scale: 1,
    rotate: 0,
    y: 0,
    transition: {
      ...SPRING_CONFIG.gentle,
    },
  },
};

export const menuIconVariant: Variants = {
  opened: {
    rotate: 180,
    scale: 1.05,
    transition: {
      ...SPRING_CONFIG.snappy,
    },
  },
  closed: {
    rotate: 0,
    scale: 1,
    transition: {
      ...SPRING_CONFIG.snappy,
    },
  },
};

export const bannerImageVariant: Variants = {
  opened: {
    scale: 1.08,
    rotate: 1,
    transition: {
      delay: ANIMATION_DELAYS.fade_in,
      ...SPRING_CONFIG.gentle,
    },
  },
  closed: {
    scale: 1,
    rotate: 0,
    transition: {
      ...SPRING_CONFIG.gentle,
    },
  },
};

export const overlayVariant: Variants = {
  opened: {
    opacity: 0.2,
    transition: {
      delay: ANIMATION_DELAYS.fade_in,
      duration: ANIMATION_DURATIONS.slow,
      ease: EASING.smooth,
    },
  },
  closed: {
    opacity: 0.6,
    transition: {
      duration: ANIMATION_DURATIONS.medium,
      ease: EASING.smooth,
    },
  },
};

export const textElementVariant: Variants = {
  opened: {
    y: 0,
    opacity: 1,
    scale: 1,
    transition: {
      delay: ANIMATION_DELAYS.fade_in + 0.2,
      ...SPRING_CONFIG.gentle,
    },
  },
  closed: {
    y: 20,
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: ANIMATION_DURATIONS.fast,
      ease: EASING.ease_in,
    },
  },
};

export const INITIAL_STATES = {
  fade_in_start: { opacity: 0, y: 10 },
  fade_in_end: { opacity: 1, y: 0 },
  slide_down: { y: "-100%" },
  slide_up: { y: "100%" },
  scale_down: { scale: 0.8 },
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  visible: { opacity: 1, scale: 1, y: 0 },
} as const;

export const FADE_IN_TRANSITION = {
  ...SPRING_CONFIG.gentle,
} as const;

export const HOVER_SPRING = {
  ...SPRING_CONFIG.snappy,
} as const;

export const HOVER_TRANSITION = {
  duration: ANIMATION_DURATIONS.fast,
  ease: EASING.ease_out,
} as const;

export const MICRO_INTERACTION = {
  duration: ANIMATION_DURATIONS.instant,
  ease: EASING.ease_out,
} as const;

export const SMOOTH_TRANSITION = {
  duration: ANIMATION_DURATIONS.medium,
  ease: EASING.smooth,
} as const;
